import { Action, Resource } from '@/types/Policy';
import { User } from '@/types/User';

function parsePermission(permissionName: string): { action: Action; resource: Resource } | null {
    const match = permissionName.match(/^(view|create|update|delete)-(.+)$/);
    if (!match) return null;
    
    return {
      action: match[1] as Action,
      resource: match[2] as Resource
    };
  }
  
  /**
   * Checks if a user has a specific permission
   */
  export function hasPermission(user: User | null, permissionName: string): boolean {
    if (!user) return false;
    
    return user.roles.some(role => 
      role.permissions.some(permission => permission.name === permissionName)
    );
  }
  
  /**
   * Checks if a user can perform a specific action on a resource
   */
  export function can(user: User | null, action: Action, resource: Resource): boolean {
    if (!user) return false;
    
    const permissionName = `${action}-${resource}`;
    return hasPermission(user, permissionName) || user.is_admin;
  }
  
  /**
   * Higher order function to check multiple permissions
   */
  export function hasAllPermissions(user: User | null, permissions: string[]): boolean {
    if (!user) return false;
    
    return permissions.every(permission => hasPermission(user, permission));
  }
  
  /**
   * Check if user has any permission for a resource
   */
  export function hasAnyResourcePermission(user: User | null, resource: Resource): boolean {
    if (!user) return false;
    
    const actions: Action[] = ['view', 'create', 'update', 'delete'];
    return actions.some(action => can(user, action, resource));
  }
  
  /**
   * Get all resources the user has access to
   */
  export function getAccessibleResources(user: User | null): Resource[] {
    if (!user) return [];
    
    const resources = new Set<Resource>();
    
    user.roles.forEach(role => {
      role.permissions.forEach(permission => {
        const parsed = parsePermission(permission.name);
        if (parsed) {
          resources.add(parsed.resource);
        }
      });
    });
    
    return Array.from(resources);
  }
  
  /**
   * Higher order function for complex policy rules
   */
  export function definePolicy(callback: (user: User | null) => boolean): (user: User | null) => boolean {
    return callback;
  }
  
  // Example of a custom policy
  export const canManageAdmins = definePolicy((user) => {
    // Only super admins can manage admin users
    return hasPermission(user, 'update-users') && hasPermission(user, 'create-users');
  });